import { DeductionDetailJson, DeductionExt, DisputePlaybook, bulk_parse_backup } from "@/api/deduction.tsx"
import { dispute_delivery_method, dispute_recipient } from "@/deduction/dispute_playbook.ts"
import {
  DeductionShipments,
  DisputeRecipients,
  DisputeType,
  add_cc,
  append_to_message,
  build_dispute_form,
  fetch_deduction_shipments,
  fetch_dispute_recipients,
  parse_email_list,
  submit_dispute,
} from "@/api/dispute.tsx"
import { Button } from "@/components/ui/button.tsx"
import { Input } from "@/components/ui/input.tsx"
import { OrderDetails } from "@/deduction_detail/ShipmentPanel.tsx"
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer.tsx"
import { Textarea } from "@/components/ui/textarea.tsx"
import { toast } from "@/components/ui/use-toast.ts"
import { DeductionFile, getDisputeAttachmentFiles, handleViewS3Uri } from "@/deduction/[id]/UserFiles.tsx"
import { Eye, File, Info, Trash2, Truck, X } from "lucide-react"
import React from "preact/compat"
import { useEffect, useState } from "preact/hooks"
import { RefreshFlag } from "@/refresh_flag.tsx"

import { entity, persistence } from "simpler-state"
import { Err, useAsyncEffect } from "@/utils"
import { format, parseISO } from "date-fns"
import { api_fetch, Backup } from "@/api"
import {
  compose_dispute_message,
  strip_dispute_amount_suffix,
} from "@/dispute/dispute_amount_suffix"
import { disputable_amount } from "@/utils/transaction_amount"

// determines if drawer is open or closed
export const DrawerDeduction = entity<DeductionExt | null>(null, [
  persistence("drawer-deduction", { storage: "session" }),
])

// Pre-filled dispute message from AI analysis
export const PrefilledDisputeMessage = entity<{ deductionId: string; message: string } | null>(null)

/**
 * Open dispute drawer with a pre-filled message
 * Call this before navigating to the deduction detail page
 */
export function setPrefilledDisputeMessage(deductionId: string, message: string) {
  PrefilledDisputeMessage.set({ deductionId, message })
}

export interface GenerateDisputeMessageResponse {
  message: string
  disputes: Array<{
    po_number: string
    check_number: string
    amount: number
    backup_row: Backup
  }>
  previously_disputed_message?: string
  total_amount?: number
  already_disputed: boolean
  needs_parse_backup?: boolean
  no_dispute_reason?: string
  /** The files this dispute needs (UNFI SAS recovery); the drawer pre-selects only these. */
  attachments?: DeductionFile[] | null
}

export interface ApplyGenerateDisputeMessageResult {
  message: string
  disputeAmount: number
  needsParseBackup: boolean
  noDisputeReason: string | null
  skipTypingAnimation: boolean
}

/** The transaction fields the default dispute amount is derived from (see `disputable_amount`). */
export type DisputeAmountSource = Pick<DeductionExt, "invoice_amount" | "source" | "kind" | "gross_amount" | "discount_amount">

function default_dispute_amount(deduction: DisputeAmountSource): number {
  return disputable_amount(deduction)
}

export function apply_generate_dispute_message_result(
  data: GenerateDisputeMessageResponse,
  deduction: DisputeAmountSource,
): ApplyGenerateDisputeMessageResult {
  if (data.needs_parse_backup) {
    return {
      message: "",
      disputeAmount: default_dispute_amount(deduction),
      needsParseBackup: true,
      noDisputeReason: null,
      skipTypingAnimation: true,
    }
  }

  if (data.no_dispute_reason) {
    return {
      message: "",
      disputeAmount: default_dispute_amount(deduction),
      needsParseBackup: false,
      noDisputeReason: data.no_dispute_reason,
      skipTypingAnimation: true,
    }
  }

  const amount = data.total_amount ?? 0
  const composed = compose_dispute_message(data.message, amount, deduction.source)
  return {
    message: composed,
    disputeAmount: amount,
    needsParseBackup: false,
    noDisputeReason: null,
    skipTypingAnimation: false,
  }
}

/** What the drawer says when a filing goes in as an appeal of a denied UNFI claim. */
export function portal_appeal_notice(appeal: { claim: string; opens_at: string }, now: Date): string {
  const opens = parseISO(appeal.opens_at)
  const when = opens > now ? ` UNFI opens appeals a few days after a denial, so this goes out on ${format(opens, "MMM d")}.` : ""
  return `UNFI denied claim ${appeal.claim}, so this goes in as its appeal, and the claim keeps its files. UNFI takes one appeal per claim.${when}`
}

export function dispute_filed_toast_description(
  category_dispute_email_to: string | null | undefined,
  playbook: DisputePlaybook | null | undefined,
): string {
  if (dispute_delivery_method(category_dispute_email_to, playbook) === "email") {
    return "Dispute email will be sent shortly. Replies will sync to this deduction."
  }
  if (playbook) {
    const where = playbook.portal_name ?? `the ${playbook.retailer} portal`
    return `Dispute recorded. ${playbook.retailer} takes disputes through ${where} — file it there and log the case here.`
  }
  return "Successfully filed dispute. Messages will be synced in a few minutes."
}

function TypingIndicator() {
  return (
    <div className="flex items-center gap-2 text-sm text-blue-600">
      <span>Writing your message</span>
      <div className="flex space-x-1">
        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
        <div
          className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"
          style="animation-delay: 0.1s"></div>
        <div
          className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"
          style="animation-delay: 0.2s"></div>
      </div>
      {/* TODO: keep the blue message up once typing is done */}
    </div>
  )
}

/** The files the drawer starts with: the server's pick for this dispute when it sent one,
 * otherwise every file on the deduction. */
export function initial_dispute_files(
  deduction: DeductionExt,
  preselected: DeductionFile[] | null | undefined,
): DeductionFile[] {
  return preselected ?? getDisputeAttachmentFiles(deduction)
}

export interface AttachmentListProps {
  deduction: DeductionExt
  /** The server's pick of files for this dispute; `null` starts from every file. */
  preselected?: DeductionFile[] | null
  files: DeductionFile[]
  setFiles: React.Dispatch<React.SetStateAction<DeductionFile[]>>
  uploadFiles: File[]
  setUploadFiles: React.Dispatch<React.SetStateAction<File[]>>
}

export function AttachmentsList(props: AttachmentListProps) {
  let { deduction, preselected, files, setFiles, uploadFiles, setUploadFiles } = props

  useEffect(() => {
    setFiles(initial_dispute_files(deduction, preselected))
  }, [deduction.id, deduction.user_files, deduction.backup_s3_uri, deduction.check_s3_uri, preselected])

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const target = event.target as HTMLInputElement
    if (!target.files?.length) return
    setUploadFiles(files => [...files, ...target.files!])
  }

  function removeFile(indexToRemove: number) {
    setFiles(files => files.filter((_, index) => index !== indexToRemove))
  }
  function removeUploadedFile(indexToRemove: number) {
    setUploadFiles(files => files.filter((_, index) => index !== indexToRemove))
  }

  return (
    <div className="flex flex-col space-y-1">
      {files.map((file, index) => (
        <div key={index} className="flex items-center justify-between">
          <div className="flex items-center">
            {/* <File className="w-4 h-4 mr-1" /> */}
            <DrawerDescription>{file.display_name}</DrawerDescription>
          </div>
          <div className="flex items-center gap-2">
            <button
              class="cursor-pointer"
              onClick={e => {
                e.preventDefault()
                handleViewS3Uri(file.s3_uri)(e as unknown as MouseEvent)
              }}>
              <Eye className="h-4 w-4" />
            </button>
            <button class="cursor-pointer" onClick={() => removeFile(index)}>
              <Trash2 className="h-4 w-4 text-red-500" />
            </button>
          </div>
        </div>
      ))}
      {uploadFiles.map((file, index) => (
        <div key={index} className="flex items-center justify-between">
          <div className="flex items-center">
            <File className="w-4 h-4 mr-1" />
            <DrawerDescription>{file.name}</DrawerDescription>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="cursor-pointer"
              onClick={e => {
                e.preventDefault()
                const url = URL.createObjectURL(file)
                window.open(url, "_blank")
                URL.revokeObjectURL(url)
              }}>
              <Eye className="h-4 w-4" />
            </button>
            <button class="cursor-pointer" onClick={() => removeUploadedFile(index)}>
              <Trash2 className="h-4 w-4 text-red-500" />
            </button>
          </div>
        </div>
      ))}

      <form method="post" encType="multipart/form-data">
        <div className="">
          <label for="image_uploads" class="btn bg-primary cursor-pointer">
            Select files
          </label>
          <input
            type="file"
            style="opacity: 0"
            id="image_uploads"
            name="image_uploads"
            multiple
            onChange={handleFileChange}
            className="mt-2"
          />
        </div>
      </form>
    </div>
  )
}

export interface DisputeRecipientsSectionProps {
  recipients: DisputeRecipients
  cc: string[]
  setCc: (cc: string[]) => void
}

/** Who an emailed dispute goes to, with the CC list the person can add to. */
export function DisputeRecipientsSection(props: DisputeRecipientsSectionProps) {
  const { recipients, cc, setCc } = props
  const [draft, setDraft] = useState("")

  function commit_draft() {
    const parsed = parse_email_list(draft)
    if (parsed.length === 0) return
    setCc(add_cc(cc, parsed, recipients.to))
    setDraft("")
  }

  function remove(addr: string) {
    setCc(cc.filter((c: string) => c !== addr))
  }

  if (recipients.error) {
    return (
      <div class="mt-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50/60 px-3 py-2 text-sm text-red-900">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-red-700" />
        <span>This dispute cannot be emailed yet: {recipients.error}</span>
      </div>
    )
  }

  return (
    <div class="mt-2 flex flex-col gap-2 rounded-md border border-blue-200 bg-blue-50/50 px-3 py-2 text-sm text-blue-900">
      <div class="flex items-start gap-2">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-700" />
        <span>
          This dispute will be sent via email to <span class="font-medium">{recipients.to}</span>.
        </span>
      </div>
      <div class="flex flex-col gap-1 pl-6">
        <div class="flex flex-wrap items-center gap-1">
          <span class="text-xs font-medium uppercase tracking-wide text-blue-800">CC</span>
          {cc.length === 0 ? <span class="text-xs text-blue-700/80">nobody yet</span> : null}
          {cc.map((addr: string) => (
            <span
              key={addr}
              class="inline-flex items-center gap-1 rounded-full border border-blue-300 bg-white px-2 py-0.5 text-xs text-blue-900">
              {addr}
              <button type="button" class="cursor-pointer" aria-label={`Remove ${addr}`} onClick={() => remove(addr)}>
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
        <div class="flex items-center gap-2">
          <Input
            value={draft}
            placeholder="Add a CC (email), press Enter"
            className="h-8 bg-white text-sm"
            onInput={(e: any) => setDraft(e.currentTarget.value)}
            onKeyDown={(e: KeyboardEvent) => {
              if (e.key === "Enter" || e.key === ",") {
                e.preventDefault()
                commit_draft()
              }
            }}
            onBlur={commit_draft}
          />
          <Button type="button" variant="outline" size="sm" onClick={commit_draft} disabled={!draft.trim()}>
            Add
          </Button>
        </div>
        {recipients.cc_note ? <span class="text-xs text-blue-800/90">{recipients.cc_note}</span> : null}
      </div>
    </div>
  )
}

export interface ShipmentSectionProps {
  shipments: DeductionShipments | null
  loading: boolean
  onInsert: (snippet: string) => void
}

/**
 * What NetSuite says about how the order shipped (UPS tracking numbers, or the freight PRO), with
 * a button to drop it into the message. When nothing is found, a box to type the tracking number in.
 */
export function ShipmentSection(props: ShipmentSectionProps) {
  const { shipments, loading, onInsert } = props
  const [manual, setManual] = useState("")

  if (loading) {
    return <DrawerDescription>Looking up the shipment in NetSuite…</DrawerDescription>
  }
  if (!shipments) return null

  const found = shipments.shipments.length > 0
  return (
    <div class="flex flex-col gap-2 rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2 text-sm text-slate-800">
      {found ? (
        <>
          {shipments.orders.map(o => (
            <OrderDetails key={o.order.id} order={o} />
          ))}
          <div>
            <Button type="button" variant="outline" size="sm" onClick={() => onInsert(shipments.message_snippet)} disabled={!shipments.message_snippet}>
              Add shipment details to message
            </Button>
          </div>
        </>
      ) : (
        <>
          <div class="flex items-start gap-2">
            <Truck className="mt-0.5 h-4 w-4 shrink-0 text-slate-600" />
            <span>{shipments.note ?? "No shipment found."}</span>
          </div>
          <div class="flex items-center gap-2 pl-6">
            <Input
              value={manual}
              placeholder="Tracking number (UPS 1Z…)"
              className="h-8 bg-white text-sm"
              onInput={(e: any) => setManual(e.currentTarget.value)}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!manual.trim()}
              onClick={() => {
                onInsert(manual_tracking_snippet(manual))
                setManual("")
              }}>
              Add to message
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

/** The sentence a hand-typed tracking number becomes in the message. */
export function manual_tracking_snippet(raw: string): string {
  const numbers = raw
    .split(/[,;\s]+/)
    .map((n: string) => n.trim())
    .filter((n: string) => n.length > 0)
  if (numbers.length === 0) return ""
  const ups = numbers.every((n: string) => n.toUpperCase().startsWith("1Z"))
  return `${ups ? "UPS tracking" : "Tracking"}: ${numbers.join(", ")}.`
}

export function FileDisputeDrawer() {
  let deduction = DrawerDeduction.use(d => d)
  let [uploading, setUploading] = useState(false)
  let [files, setFiles] = useState<DeductionFile[]>([])
  let [preselectedFiles, setPreselectedFiles] = useState<DeductionFile[] | null>(null)
  let [uploadFiles, setUploadFiles] = useState<File[]>([])
  let [validationError, setValidationError] = useState<string | null>(null)
  let [sliceMessage, setSliceMessage] = useState(0)
  let [disputeAmount, setDisputeAmount] = useState(0)
  let [message, setMessage] = useState("")
  let [needsParseBackup, setNeedsParseBackup] = useState(false)
  let [parsingBackup, setParsingBackup] = useState(false)
  let [reloadKey, setReloadKey] = useState(0)
  let [noDisputeReason, setNoDisputeReason] = useState<string | null>(null)
  let [recipients, setRecipients] = useState<DisputeRecipients | null>(null)
  let [cc, setCc] = useState<string[]>([])
  let [shipments, setShipments] = useState<DeductionShipments | null>(null)
  let [shipmentsLoading, setShipmentsLoading] = useState(false)

  // Who the email goes to, as the server will actually send it (Core-Mark's desks and the
  // purchasing manager off the fee notice, WinCo's desks, category / distributor addresses).
  useAsyncEffect(async () => {
    setRecipients(null)
    setCc([])
    setShipments(null)
    if (!deduction) return
    const type = deduction.kind === "deduction" ? DisputeType.Chargeback : DisputeType.EarlyPay
    const res = await fetch_dispute_recipients(deduction.id, type)
    if (!res.ok) return
    setRecipients(res.value.data)
    setCc(res.value.data.cc)

    // How the order shipped, from NetSuite by PO — the proof of delivery a late fee dispute needs.
    // Only a Core-Mark late fee is argued that way; no other dispute shows the block.
    if (!res.value.data.shipment_lookup || !deduction.po_number) return
    setShipmentsLoading(true)
    const shipped = await fetch_deduction_shipments(deduction.id)
    setShipmentsLoading(false)
    if (shipped.ok) setShipments(shipped.value.data)
  }, [deduction?.id])

  useAsyncEffect(async () => {
    if (!deduction) return

    const source = deduction.source
    setPreselectedFiles(null)

    // Check for pre-filled message from bulk analysis
    const prefilled = PrefilledDisputeMessage.get()
    if (prefilled && prefilled.deductionId === deduction.id) {
      PrefilledDisputeMessage.set(null) // Clear after use

      // Still need to get dispute amount from API
      let res = await api_fetch<GenerateDisputeMessageResponse>(
        `/deduction/${deduction.id}/generate-dispute-message`
      )
      const amount = res.ok && res.value.data.total_amount ? res.value.data.total_amount : 0
      if (res.ok) setPreselectedFiles(res.value.data.attachments ?? null)
      const composed = compose_dispute_message(prefilled.message, amount, source)
      setDisputeAmount(amount)
      setMessage(composed)
      setSliceMessage(composed.length) // Skip typing animation
      return
    }

    setMessage("")
    setNeedsParseBackup(false)
    setNoDisputeReason(null)
    let res = await api_fetch<GenerateDisputeMessageResponse>(
      `/deduction/${deduction.id}/generate-dispute-message`
    )
    if (!res.ok) {
      setValidationError(res.error.message)
      return
    }
    let data = res.value.data
    const applied = apply_generate_dispute_message_result(data, deduction)
    setNeedsParseBackup(applied.needsParseBackup)
    setNoDisputeReason(applied.noDisputeReason)
    setDisputeAmount(applied.disputeAmount)
    setPreselectedFiles(data.attachments ?? null)
    setMessage(applied.message)
    setSliceMessage(applied.skipTypingAnimation ? applied.message.length : 0)

    // Refetch the deduction to get updated user_files after auto-dispute processing
    if (!applied.needsParseBackup && !applied.noDisputeReason) {
      let deductionRes = await api_fetch<DeductionDetailJson>(`/deduction/${deduction.id}`)
      if (deductionRes.ok) {
        DrawerDeduction.set(deductionRes.value.data)
      }
    }

  }, [deduction?.id, reloadKey])

  useEffect(() => {
    if (sliceMessage >= message.length) return

    const timer = setTimeout(() => {
      setSliceMessage(prev => prev + 1)
    }, 10)

    return () => clearTimeout(timer)
  }, [sliceMessage, message])

  function handleDisputeAmountChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (!deduction) return

    const target = event.target as HTMLInputElement
    const new_amount = Number.parseFloat(target.value) || 0
    const composed = compose_dispute_message(
      strip_dispute_amount_suffix(message, disputeAmount),
      new_amount,
      deduction.source,
    )
    setMessage(composed)
    setSliceMessage(composed.length)
    setDisputeAmount(new_amount)
  }

  async function handleParseBackup() {
    if (!deduction) return
    setParsingBackup(true)
    let res = await bulk_parse_backup([deduction.id])
    setParsingBackup(false)
    if (res.ok) {
      // Re-run dispute message generation now that POs may be correlated from the backup.
      setReloadKey(k => k + 1)
    } else {
      toast({
        title: "Failed to parse backup",
        description: res.error.message,
        variant: "destructive",
      })
    }
  }

  async function handleFileDispute() {
    if (!disputeAmount) {
      return
    }
    if (!message) {
      return
    }
    setUploading(true)
    let res: Awaited<ReturnType<typeof submit_dispute>>
    try {
      let form = build_dispute_form({
        deduction: deduction!,
        message: message,
        dispute_amount: disputeAmount,
        files: uploadFiles,
        existing_file_uris: files.map(f => f.s3_uri),
        cc,
      })
      res = await submit_dispute(form)
    } catch (e) {
      res = Err(e instanceof Error ? e : new Error(String(e)))
    } finally {
      // The drawer stays mounted across deductions: a flag left on disables Submit for every later one.
      setUploading(false)
    }
    if (res.ok) {
      toast({
        title: "Dispute filed",
        description: dispute_filed_toast_description(
          deduction!.category_dispute_email_to,
          deduction!.dispute_playbook,
        ),
      })
      console.log("Dispute filed successfully")
    } else {
      toast({
        title: "Error",
        description: res.error.message,
        variant: "destructive",
      })
    }
    RefreshFlag.set(flag => !flag)
  }
  if (!deduction) {
    return null
  }

  let invoice_number = deduction.invoice_number
  let playbook = deduction.dispute_playbook
  let dispute_email_to = dispute_recipient(deduction.category_dispute_email_to, playbook)
  let server_email = recipients?.channel === "email" ? recipients : null
  let portal_playbook = !server_email && !dispute_email_to && playbook?.channel === "portal" ? playbook : null
  let displayMessage = message.slice(0, sliceMessage)
  let isTyping = message.length > sliceMessage
  return (
    <Drawer open={true} onOpenChange={open => (open ? null : DrawerDeduction.set(null))}>
      <DrawerContent>
        {/* The body scrolls on its own so a tall form (e.g. a shipment block) never pushes Submit off-screen. */}
        <DrawerHeader className="min-h-0 overflow-y-auto">
          <DrawerTitle>File Dispute</DrawerTitle>
          <DrawerDescription>
            Review, edit, and upload relevant files then hit submit to file dispute.
          </DrawerDescription>

          {server_email ? (
            <DisputeRecipientsSection recipients={server_email} cc={cc} setCc={setCc} />
          ) : dispute_email_to ? (
            <div class="mt-2 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50/50 px-3 py-2 text-sm text-blue-900">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-700" />
              <span>This dispute will be sent via email to {dispute_email_to}.</span>
            </div>
          ) : null}
          {recipients?.refusal ? (
            <div class="mt-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50/60 px-3 py-2 text-sm text-red-900">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-red-700" />
              <span>{recipients.refusal}</span>
            </div>
          ) : null}
          {recipients?.portal_appeal ? (
            <div class="mt-2 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50/60 px-3 py-2 text-sm text-blue-900">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-700" />
              <span>{portal_appeal_notice(recipients.portal_appeal, new Date())}</span>
            </div>
          ) : null}
          {portal_playbook ? (
            <div class="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-sm text-amber-900">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
              <span>
                {portal_playbook.retailer} takes disputes only through{" "}
                {portal_playbook.portal_url ? (
                  <a href={portal_playbook.portal_url} target="_blank" rel="noreferrer" class="underline">
                    {portal_playbook.portal_name ?? "its portal"}
                  </a>
                ) : (
                  portal_playbook.portal_name ?? "its portal"
                )}
                . Submitting here records the dispute and starts the clock; file it in the portal using the
                steps under "How to dispute" on the deduction page.
              </span>
            </div>
          ) : null}

          <DrawerTitle className="text-md pt-2">Title</DrawerTitle>
          <DrawerDescription>
            {`Supplier Dispute Form - Invoice #: ${invoice_number}`}
          </DrawerDescription>

          <div class="flex items-center mt-2 gap-2">
            <DrawerTitle className="text-md">Message</DrawerTitle>
            {isTyping && <TypingIndicator />}
          </div>
          {needsParseBackup ? (
            <div class="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-3 text-sm text-amber-900">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
              <div class="flex flex-col gap-2">
                <span>
                  We couldn't find any PO numbers in this deduction's backup yet, so there's no
                  dispute message to generate. Parse the backup so the system can correlate PO
                  numbers with previous deductions.
                </span>
                <span>You can still file a dispute manually below.</span>
                <div>
                  <Button
                    variant="default"
                    onClick={handleParseBackup}
                    disabled={parsingBackup}>
                    {parsingBackup ? "Parsing..." : "Parse Backup"}
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
          {noDisputeReason ? (
            <div class="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50/50 px-3 py-3 text-sm text-blue-900">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-700" />
              <div class="flex flex-col gap-1">
                <span>{noDisputeReason}</span>
                <span>You can still file a dispute manually below.</span>
              </div>
            </div>
          ) : null}
          <div className="">
            <Textarea
              value={displayMessage}
              onFocus={() => setSliceMessage(message.length)}
              onChange={(e: any) => {
                setMessage(e.target.value)
                setSliceMessage(e.target.value.length)
              }}
              className={`border border-plue-100 hover:border-plue-500 transition-all ${isTyping ? "border-blue-300 bg-blue-50/30" : ""}`}
              placeholder="Enter your dispute message"
            />
          </div>

          {deduction.po_number && (shipmentsLoading || shipments) ? (
            <>
              <DrawerTitle className="text-md pt-2">Shipment (PO {deduction.po_number})</DrawerTitle>
              <ShipmentSection
                shipments={shipments}
                loading={shipmentsLoading}
                onInsert={(snippet: string) => {
                  const next = append_to_message(message, snippet)
                  setMessage(next)
                  setSliceMessage(next.length)
                }}
              />
            </>
          ) : null}

          <DrawerTitle className="text-md pt-2">Dispute amount</DrawerTitle>
          <div className="flex items-center gap-2">
            <input
              type="number"
              step="0.01"
              value={disputeAmount}
              onChange={handleDisputeAmountChange}
              className="border border-plue-100 hover:border-plue-500 p-1 rounded w-36 text-right"
            />
          </div>
          <DrawerTitle className="text-md pt-2">Attachments</DrawerTitle>
          <AttachmentsList
            deduction={deduction}
            preselected={preselectedFiles}
            files={files}
            setFiles={setFiles}
            uploadFiles={uploadFiles}
            setUploadFiles={setUploadFiles}
          />
          {validationError && (
            <div className="text-red-500 text-sm mt-2 mb-2">{validationError}</div>
          )}
        </DrawerHeader>

        <DrawerFooter className="flex flex-row items-start border-t">
          <DrawerClose asChild={true}>
            <Button
              onClick={handleFileDispute}
              disabled={
                uploading || !!validationError || !!recipients?.refusal || isTyping || !disputeAmount || !message.trim()
              }
              variant="default">
              {uploading ? "Uploading..." : "Submit"}
            </Button>
          </DrawerClose>
          <DrawerClose asChild={true}>
            <Button variant="outline">Cancel</Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("portal_appeal_notice", () => {
    it("names the claim and the day it goes out while the wait runs", () => {
      const text = portal_appeal_notice({ claim: "187832", opens_at: "2026-10-15T19:00:00Z" }, new Date("2026-10-13T00:00:00Z"))
      expect(text).toContain("claim 187832")
      expect(text).toContain("Oct 15")
    })

    it("names no day once the wait is over", () => {
      const text = portal_appeal_notice({ claim: "187832", opens_at: "2026-10-15T19:00:00Z" }, new Date("2026-10-30T00:00:00Z"))
      expect(text).not.toContain("goes out on")
    })
  })

  describe("dispute_filed_toast_description", () => {
    const costco: DisputePlaybook = {
      retailer: "Costco",
      deduction_type: "Costco deduction (RV)",
      channel: "portal",
      dispute_email: null,
      backup_email: null,
      portal_name: "Vendor Accounting Portal (Costco Hub)",
      portal_url: "https://apvendor.costco.com",
      steps: [],
      backup_steps: [],
      notes: [],
    }

    it("uses email copy when category_dispute_email_to is set", () => {
      expect(dispute_filed_toast_description("disputes@kehe.com", null)).toContain("Dispute email")
    })

    it("tells the person to file in the portal for a portal-only retailer", () => {
      expect(dispute_filed_toast_description(null, costco)).toContain("Vendor Accounting Portal")
    })

    it("uses portal-integration copy with no playbook", () => {
      expect(dispute_filed_toast_description(null, null)).toContain("Messages will be synced")
    })
  })

  describe("manual_tracking_snippet", () => {
    it("labels UPS numbers and lists several", () => {
      expect(manual_tracking_snippet(" 1ZF2C1544235239744, 1zf2c1544228437954 ")).toBe(
        "UPS tracking: 1ZF2C1544235239744, 1zf2c1544228437954.",
      )
      expect(manual_tracking_snippet("PHORM13719048")).toBe("Tracking: PHORM13719048.")
      expect(manual_tracking_snippet("  ")).toBe("")
    })
  })

  describe("apply_generate_dispute_message_result", () => {
    const deduction: DisputeAmountSource = { invoice_amount: -150.5, source: "unfi", kind: "deduction", gross_amount: -150.5, discount_amount: 0 }

    it("defaults a UNFI shortage that netted a terms discount to its gross", () => {
      // Saffron Road's INV075319: gross -164.00, discount +3.28, net -160.72 — the adjustment is for the gross.
      const shortage: DisputeAmountSource = { invoice_amount: -160.72, source: "unfi", kind: "deduction", gross_amount: 164, discount_amount: -3.28 }
      const result = apply_generate_dispute_message_result(
        { message: "", disputes: [], already_disputed: false, no_dispute_reason: "No matched UNFI repayment yet." },
        shortage,
      )
      expect(result.disputeAmount).toBe(164)
    })

    it("returns warning and manual defaults when no_dispute_reason is set", () => {
      const result = apply_generate_dispute_message_result(
        {
          message: "",
          disputes: [],
          already_disputed: false,
          no_dispute_reason: "No matched UNFI repayment yet.",
        },
        deduction,
      )
      expect(result.noDisputeReason).toBe("No matched UNFI repayment yet.")
      expect(result.needsParseBackup).toBe(false)
      expect(result.message).toBe("")
      expect(result.disputeAmount).toBe(150.5)
      expect(result.skipTypingAnimation).toBe(true)
    })

    it("returns parse flag and manual defaults when needs_parse_backup is set", () => {
      const result = apply_generate_dispute_message_result(
        {
          message: "",
          disputes: [],
          already_disputed: false,
          needs_parse_backup: true,
        },
        deduction,
      )
      expect(result.needsParseBackup).toBe(true)
      expect(result.noDisputeReason).toBe(null)
      expect(result.message).toBe("")
      expect(result.disputeAmount).toBe(150.5)
      expect(result.skipTypingAnimation).toBe(true)
    })

    it("returns composed auto message on success", () => {
      const result = apply_generate_dispute_message_result(
        {
          message: "Hi, please reimburse the audit.",
          disputes: [],
          already_disputed: false,
          total_amount: 75.25,
        },
        deduction,
      )
      expect(result.needsParseBackup).toBe(false)
      expect(result.noDisputeReason).toBe(null)
      expect(result.message).toBe("Hi, please reimburse the audit.")
      expect(result.disputeAmount).toBe(75.25)
      expect(result.skipTypingAnimation).toBe(false)
    })
  })

  describe("initial_dispute_files", () => {
    const deduction = {
      id: "d",
      source: "unfi",
      customer_name: "UNFI",
      backup_s3_uri: "s3://b/org/o/thread/t/m/SASIF20261149232.pdf",
      check_s3_uri: "s3://b/org/o/thread/t/m/366306.pdf",
      user_files: [
        {
          display_name: "PO 74299957.pdf",
          s3_uri: "s3://b/org/o/user_files/deduction/d/PO 74299957.pdf",
          date: "2026-10-02T01:42:50Z",
        },
      ],
    } as unknown as DeductionExt

    it("starts from every file on the deduction when the server picked none", () => {
      expect(initial_dispute_files(deduction, null).map((f: DeductionFile) => f.s3_uri)).toEqual([
        "s3://b/org/o/user_files/deduction/d/PO 74299957.pdf",
        "s3://b/org/o/thread/t/m/SASIF20261149232.pdf",
        "s3://b/org/o/thread/t/m/366306.pdf",
      ])
    })

    it("starts from only the server's pick for a SAS recovery dispute", () => {
      const picked: DeductionFile[] = [
        { display_name: "UNFI Backup", s3_uri: "s3://b/org/o/thread/t/m/SASIF20261149232.pdf" },
        { display_name: "Check #1736694 for PO #74299957", s3_uri: "s3://b/org/o/thread/t/m/CK_1736694.pdf" },
      ]
      expect(initial_dispute_files(deduction, picked)).toEqual(picked)
    })
  })
}
